import { useTranslation } from 'react-i18next';

import { Spinner } from '@/components/atoms/Spinner';
import ContentBlock from '@/components/molecules/ContentBlock';
import EmptyState from '@/components/molecules/EmptyState';
import { retryFailedQueries } from '@/components/retryFailedQueries';
import SomethingHappened from '@/components/SomethingHappened';
import { useGetClientFishGroupsOverview } from '@/domain/fishGroups';
import { usePostHogFeatureFlag } from '@/hooks/usePostHogFeatureFlag';

import FishGroupsOverview from './FishGroupsOverview';

interface Props {
    clientId: number;
}

export default function FishGroupsOverviewContainer(props: Props) {
    const { clientId } = props;
    const { t } = useTranslation('fishGroups');
    const { enabled: isEnabled, isLoading: isFlagLoading } =
        usePostHogFeatureFlag('fish-group-tracking');

    const overviewQuery = useGetClientFishGroupsOverview(clientId, {
        enabled: isEnabled,
    });
    const { data, isLoading, error } = overviewQuery;

    // Render nothing until PostHog has resolved the flag, so the block never flashes in.
    if (isFlagLoading || !isEnabled) {
        return null;
    }

    if (isLoading) {
        return (
            <ContentBlock title={t('overview.title')}>
                <Spinner size="lg" />
            </ContentBlock>
        );
    }

    if (error) {
        return (
            <ContentBlock title={t('overview.title')}>
                <SomethingHappened
                    title={t('overview.loadingError')}
                    {...retryFailedQueries(overviewQuery)}
                />
            </ContentBlock>
        );
    }

    if (!data || (data.activeLocations.length === 0 && data.inactiveLocations.length === 0)) {
        return (
            <ContentBlock title={t('overview.title')}>
                <EmptyState title={t('overview.empty')} appearance="quiet" size="spacious" />
            </ContentBlock>
        );
    }

    return <FishGroupsOverview overview={data} />;
}
